import { RadarControls } from "./components/RadarControls";
import { Watchlist } from "./components/Watchlist";
import { due, radarQueue, type RadarView } from "./lib/radar";
import { lazy, Suspense, useMemo, useState } from "react";
import { SlidersHorizontal } from "lucide-react";
import { FilterPanel } from "./components/FilterPanel";
import { Header } from "./components/Header";
import { JobDetail } from "./components/JobDetail";
import { SignalLegend } from "./components/SignalLegend";
import { SignalQueue } from "./components/SignalQueue";
import { StatusLayer } from "./components/StatusLayer";
import { useJobs } from "./hooks/useJobs";
import {
  DEFAULT_FILTERS,
  dedupeJobs,
  filterJobs,
  getRegionOptions,
  type JobFilters,
} from "./lib/jobs";

const GlobeCanvas = lazy(() =>
  import("./components/GlobeCanvas").then((module) => ({ default: module.GlobeCanvas })),
);

export default function App() {
  const { jobs, status, error, mode, updatedAt, reload } = useJobs();
  const [view, setView] = useState<RadarView>("Apply Now");
  const [filters, setFilters] = useState<JobFilters>(DEFAULT_FILTERS);
  const [selectedJobId, setSelectedJobId] = useState<string | null>(null);
  const [filtersOpen, setFiltersOpen] = useState(false);

  const uniqueJobs = useMemo(() => dedupeJobs(jobs), [jobs]);
  const visibleJobs = useMemo(() => filterJobs(uniqueJobs, filters), [uniqueJobs, filters]);
  const regions = useMemo(() => getRegionOptions(uniqueJobs), [uniqueJobs]);
  const queueJobs = useMemo(() => radarQueue(view === "Pipeline" ? uniqueJobs : visibleJobs, view), [uniqueJobs, visibleJobs, view]);
  const selectedJob = uniqueJobs.find((job) => job.id === selectedJobId) ?? null;

  return (
    <main className="app-shell">
      <Suspense fallback={<div className="globe-stage globe-fallback" aria-label="Loading 3D globe" />}>
        <GlobeCanvas
          jobs={visibleJobs}
          selectedJobId={selectedJob?.id ?? null}
          onSelectJob={(job) => setSelectedJobId(job.id)}
        />
      </Suspense>
      <div className="interface-grid" aria-hidden="true" />
      <Header
        visibleCount={visibleJobs.length}
        totalCount={uniqueJobs.length}
        mode={mode}
        loading={status === "loading"}
      />

      <section className="radar-toolbar" aria-label="First-to-Apply Radar">
        <div><strong>First-to-Apply Radar</strong><span>{uniqueJobs.filter(j => j.pipeline?.stage === "Interview").length} interviews · {uniqueJobs.filter(j => due(j)).length} follow-ups due</span></div>
        <nav aria-label="Radar views">{(["Apply Now", "Pipeline", "All signals", "Watchlist"] as RadarView[]).map(v => <button key={v} aria-pressed={view === v} onClick={() => { setView(v); setSelectedJobId(null); }}>{v}</button>)}</nav>
        <RadarControls onReload={() => void reload()} />
      </section>
      <FilterPanel
        filters={filters}
        regions={regions}
        open={filtersOpen}
        onChange={setFilters}
        onClose={() => setFiltersOpen(false)}
      />

      <StatusLayer
        status={status}
        error={error}
        empty={status === "ready" && visibleJobs.length === 0 && view === "All signals"}
        onRetry={() => void reload()}
        onOpenFilters={() => setFiltersOpen(true)}
      />

      {!selectedJob && status === "ready" && view !== "Watchlist" && (
        <SignalQueue view={view} jobs={queueJobs} onSelect={(job) => setSelectedJobId(job.id)} />
      )}
      {!selectedJob && view === "Watchlist" && <Watchlist />}
      <JobDetail key={selectedJob?.id} onSaved={() => void reload()} job={selectedJob} onClose={() => setSelectedJobId(null)} />
      <SignalLegend />

      <div className="viewport-caption">
        <span>drag to orbit</span><i /><span>scroll to resolve</span>
      </div>
      <div className="sync-caption">
        {updatedAt ? `field synchronized ${updatedAt.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}` : "awaiting field data"}
      </div>

      <button className="mobile-filter-button" type="button" onClick={() => setFiltersOpen(true)}>
        <SlidersHorizontal size={16} /> Filters
        <span>{visibleJobs.length}</span>
      </button>
      {filtersOpen && <button className="panel-backdrop" type="button" onClick={() => setFiltersOpen(false)} aria-label="Close filters" />}
    </main>
  );
}
